import { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Stack, Typography } from "@mui/material"
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import BaseButton from "../BaseButton"
import BaseSelect from "../BaseSelect";
import BaseCheckbox from "../BaseCheckbox";
import BaseSwitch from "../BaseSwitch";

import { setMedicationFilterOptions } from "../../store";

import { EVIDENCE_LEVELS } from "../../constants";

const MedicationFilterForm = ({ sx, onClose }) => {
  const dispatch = useDispatch()
  const reports = useSelector(state => state.patientReports)
  const medicationData = useMemo(() => reports.find(item => item.name === 'Medications'), [reports])
  const resFilterOptions = useSelector(state => state.medicationFilterOptions)

  const [filterOptions, setFilterOptions] = useState()
  const [evidenceLevel, setEvidenceLevel] = useState(EVIDENCE_LEVELS[0].title)
  const [riskFactorLevels, setRiskFactorLevels] = useState([])

  const categoryOptions = useMemo(() => {
    const result = []
    if (medicationData?.data?.medications) {
      medicationData?.data?.medications.forEach(item => {
        if (!result.find(category => category.value === item.category)) {
          result.push({
            label: item.category,
            value: item.category
          })
        }
      })
    }
    return result
  }, [medicationData])

  const classOptions = useMemo(() => {
    const result = []
    if (medicationData?.data?.medications) {
      medicationData?.data?.medications.forEach(item => {
        if (!result.find(option => option.value === item.class)) {
          result.push({
            label: item.class,
            value: item.class
          })
        }
      })
    }
    return result
  }, [medicationData])

  const geneOptions = useMemo(() => {
    const result = []
    if (medicationData?.data?.medications) {
      medicationData?.data?.medications.forEach(item => {
        item.genes.forEach(gene => {
          if (!result.find(option => option.value === gene)) {
            result.push({
              label: gene,
              value: gene
            })
          }
        })
      })
    }
    return result
  }, [medicationData])

  useMemo(() => {
    setFilterOptions(resFilterOptions)
    if (resFilterOptions) {
      if (resFilterOptions?.evidenceLevel) setEvidenceLevel(resFilterOptions?.evidenceLevel)
      if (resFilterOptions?.riskFactorLevels) setRiskFactorLevels(resFilterOptions?.riskFactorLevels)
    }
  }, [resFilterOptions])

  const handleChangeSelect = (value, name) => {
    setFilterOptions({
      ...filterOptions,
      [name]: value
    })
  }

  const handleChangeRiskFactorLevel = (name) => {
    const temp = [...riskFactorLevels]
    const index = temp.indexOf(name);
    
    if (index > -1) { // only splice array when item is found
      temp.splice(index, 1); // 2nd parameter means remove one item only
      setRiskFactorLevels(temp)
    } else {
      setRiskFactorLevels([...temp, name])
    }
  }

  const handleClickFilter = () => {
    const options = {
      ...filterOptions,
      evidenceLevel: evidenceLevel === 'Low',
      riskFactorLevels
    }
    dispatch(setMedicationFilterOptions(options))
    onClose()
  }

  const handleResetFilter = () => {
    dispatch(setMedicationFilterOptions({
      category: [],
      class: [],
      gene: [],
      riskFactorLevels: [],
      guidanceLevelSortDir: resFilterOptions?.guidanceLevelSortDir,
      alphabetSortDir: resFilterOptions?.alphabetSortDir,
    }))
    onClose()
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '350px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        ...sx
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <TuneIcon
            sx={{
              fontSize: '20px',
              color: 'primary.mainBlue'
            }}
          />
          <Typography
            variant="h4"
            color="primary.black"
            sx={{
              ml: '4px'
            }}
          >
            Filter
          </Typography>
        </Box>
        <CloseIcon
          sx={{
            fontSize: '24px',
            color: 'primary.mediumGray',
            cursor: 'pointer'
          }}
          onClick={onClose}
        />
      </Box>
      <Stack
        spacing={'32px'}
        mt='32px'
      >
        <BaseSelect
          multiple
          label='Category'
          name='category'
          value={filterOptions?.category}
          options={categoryOptions}
          onChangeSelect={handleChangeSelect}
        />
        <BaseSelect
          multiple
          label='Class'
          name='class'
          value={filterOptions?.class}
          options={classOptions}
          onChangeSelect={handleChangeSelect}
        />
        <BaseSelect
          multiple
          label='Gene'
          name='gene'
          value={filterOptions?.gene}
          options={geneOptions}
          onChangeSelect={handleChangeSelect}
        />
        <Box>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: '18px'
          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center'
            }}>
              <Typography variant='h3' color='primary.darkGray'>
                Risk factor level
              </Typography>
            </Box>
            <Box
              sx={{
                cursor: 'pointer',
              }}
              onClick={() => setRiskFactorLevels([])}
            >
              <Typography variant='body' color='primary.mainBlue'>
                Clear
              </Typography>
            </Box>
          </Box>
          <Stack direction={{ xs: 'row', md: 'row'}} spacing={{ xs: '4px', md: '12px' }}>
            <BaseCheckbox
              label='Low'
              checked={riskFactorLevels.includes('low')}
              onChange={() => handleChangeRiskFactorLevel('low')}
            />
            <BaseCheckbox
              label='Moderate'
              checked={riskFactorLevels.includes('moderate')}
              onChange={() => handleChangeRiskFactorLevel('moderate')}
            />
            <BaseCheckbox
              label='High'
              checked={riskFactorLevels.includes('high')}
              onChange={() => handleChangeRiskFactorLevel('high')}
            />
          </Stack>
        </Box>
        <Box
          sx={{
            display: 'none'
          }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            mb: '18px'
          }}>
            <Typography variant='h3'>
              Evidence level
            </Typography>
          </Box>
          <BaseSwitch
            sx={{ mt: '8px' }}
            selected={evidenceLevel}
            onChange={(value) => setEvidenceLevel(value)}
            options={EVIDENCE_LEVELS}
            activeColor={evidenceLevel === 'Low' ? '#39B195' : '#E73E3E'}
          />
        </Box>
      </Stack>
      <Box
        sx={{
          mt: '40px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box
          sx={{
            cursor: 'pointer'
          }}
          onClick={handleResetFilter}
        >
          <Typography variant="body">
            Reset
          </Typography>
        </Box>
        <BaseButton
          buttonType='primary'
          width='auto'
          onClick={handleClickFilter}
        >
          <Typography
            variant="body"
            color="white"
          >
            Apply
          </Typography>
        </BaseButton>
      </Box>
    </Box>
  )
}

export default MedicationFilterForm